import React, {useState} from 'react';
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import Icon from "components/Icon";
import {invalidate, prefetchNearbyPages} from "services/query-client";
import {TemplateIcon} from "@heroicons/react/outline";
import Button from "components/Button";
import {PlusIcon} from "@heroicons/react/solid";
import {useNotifications} from "context/NotificationContext";
import {useNavigate} from "react-router-dom";
import ConfirmModal from "components/ConfirmModal";
import {truncate} from "services/string";
import {useOperator} from "context/OperatorContext";
import {FacilityType} from 'types/FacilityType';
import {formattedDateTime} from 'services/date';
import {useAuth} from "../../../context/AuthContext";
import FacilityTypeContextMenu from "../../../components/menus/FacilityTypeContextMenu";
import NotAuthorised from "../../NotAuthorised";

// Component ----

function FacilityTypeList() {
  const {termFor} = useOperator();
  const auth = useAuth();
  const {notify} = useNotifications();
  const navigate = useNavigate();

  const [deletingFacilityType, setDeletingFacilityType] = useState<FacilityType | null>(null);
  const [page, setPage] = useState<number>(1);

  const result = useQuery(
    ['facility-types.all', {page}],
    () => ngage.facilityTypes.all(page),
    {keepPreviousData: true, enabled: auth.can('facility-types:read')}
  );

  const {mutateAsync: deleteFacilityType} = useMutation(ngage.facilityTypes.destroy)

  // Prepare action handlers
  async function handleDeleteFacilityType(facilityType: FacilityType) {
    if (facilityType.id) {
      try {
        await deleteFacilityType(facilityType.id)

        setDeletingFacilityType(null)
        notify("success", `${termFor('Facility Type')} Deleted`, `${facilityType.name} has been permanently removed`)

        return invalidate('facility-types.all')
      } catch (error) {
        setDeletingFacilityType(null)
        notify("error", `${termFor('Facility Type')} Not Deleted`, `${facilityType.name} could not be removed`)
      }
    }
  }

  // Prevent unauthorised viewing
  if (auth.cannot('facility-types:read')) {
    return <NotAuthorised/>
  }

  // Prefetch next and previous pages
  prefetchNearbyPages('facility-types.all', page, ngage.facilityTypes.all)

  // Prepare page actions
  const actions = (
    <>
      {auth.can('facility-types:write') &&
        <Button icon={PlusIcon} onClick={() => navigate('/facility-types/new')}>New</Button>
      }
    </>
  )

  const breadcrumbs = {'App Content': '/', 'Site': '/', [termFor('Facility Types')]: '/facility-types'}

  return (
    <Page
      title={termFor('Facility Types')}
      actions={actions}
      breadcrumbs={breadcrumbs}
    >
      <Table paginationData={result.data} setPage={setPage}>
        <TableHead>
          <TableRow>
            <TableHeader>Name</TableHeader>
            <TableHeader>Description</TableHeader>
            <TableHeader alignment={TableHeader.alignments.RIGHT}>Facilities</TableHeader>
            <TableHeader>Added</TableHeader>
            <TableHeader/>
          </TableRow>
        </TableHead>

        <TableBody isLoading={result.isLoading}>

          {result?.data && result.data.data.length < 1 ?
            <TableRow index={0}>
              <TableData colSpan={5}>
                <div className="py-4 flex flex-col justify-center items-center">
                  <TemplateIcon className="h-8 w-8 text-gray-400 mb-2"/>
                  <span className="text-sm font-medium text-gray-600">No {termFor('facility types')}</span>
                </div>
              </TableData>
            </TableRow>
            : result?.data?.data.map((facilityType, index) => (
              <TableRow key={facilityType.id} index={index} highlight>
                <TableData>
                  <div className="flex">
                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                      <FacilityTypeContextMenu
                        facilityType={facilityType}
                        onDeleteFacilityType={() => setDeletingFacilityType(facilityType)}
                        deleteDisabled={facilityType.facility_count > 0}
                        position="left"
                      />
                    </div>

                    <div className="flex group-hover:translate-x-1 transition-all">
                      <Icon
                        name={facilityType.icon}
                        className="h-5 w-5 mr-2 text-gray-500"
                      />
                      {facilityType.name}
                    </div>
                  </div>
                </TableData>
                <TableData>{truncate(facilityType.description, 35)}</TableData>
                <TableData alignment={TableData.alignments.RIGHT}>{facilityType.facility_count}</TableData>
                <TableData>{formattedDateTime(facilityType.created_at)}</TableData>
                <TableData alignment={TableData.alignments.RIGHT}>
                  <FacilityTypeContextMenu
                    facilityType={facilityType}
                    onDeleteFacilityType={() => setDeletingFacilityType(facilityType)}
                    deleteDisabled={facilityType.facility_count > 0}
                  />
                </TableData>

                {/* Delete modal */}
                <ConfirmModal
                  show={deletingFacilityType === facilityType}
                  type={ConfirmModal.types.DANGER}
                  title={`Delete ${termFor('Facility Type')}`}
                  body={`Are you sure you want to permanently delete '${facilityType.name}'? This action cannot be undone.`}
                  approveLabel="Delete"
                  onApprove={() => handleDeleteFacilityType(facilityType)}
                  onCancel={() => setDeletingFacilityType(null)}
                />
              </TableRow>
            ))}

        </TableBody>
      </Table>
    </Page>
  );
}

export default FacilityTypeList;